// Companies workspace — split between active (industry-classified, owned)
// and lead_pool (uncategorized, marketing's playground).
// Bulk-select + Promote moves rows out of lead_pool into active.

// Same SORT_EXTRACT pattern used in deals.jsx — keys match col.key strings
// below, presence here makes the header clickable. Industry/owner extract
// strings for case-insensitive sort; numbers sort by value with nulls last.
const COMPANY_SORT_EXTRACT = {
  name:     (c) => (c.name || '').toLowerCase(),
  industry: (c) => (c.industry === '—' ? '' : (c.industry || '').toLowerCase()),
  revenue:  (c) => c.revenue,
  ebitda:   (c) => c.ebitda,
  interactions: (c) => c.interactions || 0,
  webVisits:    (c) => c.webVisits || 0,
  hq:       (c) => (c.hq === '—' ? '' : (c.hq || '').toLowerCase()),
  owner:    (c) => (c.owner === '—' ? '' : (c.owner || '').toLowerCase()),
  // DealCloud's record dates (dc_created_at / dc_modified_at) — see the note
  // in data.jsx for why Atlas's own created_at/updated_at aren't used here.
  ...window.recordDateSort(),
  // Opt-in columns (COMPANY_COLUMNS, def:false). Most are in
  // COMPANY_SERVER_SORT below and only fall back to these extractors for the
  // export path; `team` is a lookup name and sorts client-side for real.
  website:       (c) => (c.domain || '').toLowerCase(),
  hqCity:        (c) => ((c._raw || {}).hq_city || '').toLowerCase(),
  hqState:       (c) => ((c._raw || {}).hq_state || '').toLowerCase(),
  team:          (c) => (c.team || '').toLowerCase(),
  pipelineState: (c) => (c.pipelineState || '').toLowerCase(),
  trackerStage:  (c) => (c.trackerStage || '').toLowerCase(),
  lastInteraction: (c) => c.lastInteractionAt || '',
  lastWebVisit:  (c) => c.lastWebVisitAt || '',
  // Lead source (142) — a lookup name, client-side like industry/owner.
  leadSource:    (c) => (c.leadSource === '—' ? '' : (c.leadSource || '').toLowerCase()),
};

// Sort keys the server can order by (real columns). Industry/owner are
// lookup names — those two still sort client-side over the loaded pages.
const COMPANY_SERVER_SORT = {
  name: 'name', revenue: 'revenue_est', ebitda: 'ebitda_est', hq: 'hq_state',
  interactions: 'interaction_count',
  // Opt-in columns backed by plain company columns — see _ORDER_COLS in
  // routes/companies.py. Team stays client-side (a lookup name, like
  // industry and owner).
  website: 'website', hqCity: 'hq_city', hqState: 'hq_state',
  pipelineState: 'pipeline_state', trackerStage: 'tracker_stage',
  lastInteraction: 'last_interaction_at',
  webVisits: 'web_visit_count', lastWebVisit: 'last_web_visit_at',
};

const dcLocked = (c, field) => (c._raw?.dc_synced_fields || []).includes(field);

// Identifier columns appended to every company export regardless of layout,
// so a file can always be joined back to Atlas. Everything else comes from
// the visible columns — see window.exportColumnsFor.
const COMPANY_EXPORT_ALWAYS = [
  { key: 'id', label: 'Atlas id', get: (c) => c.id },
];

// ────────────────────────────────────────────────────────────────────────
// The company column catalog — one definition per column (label, width
// sample, cell), mapped by BOTH the header and the row so the two cannot
// drift. `def: false` ships the column hidden but offerable in the Columns
// menu. render(c, ctx, col) must size its outer element to col.w.
const COMPANY_COLUMNS = [
  {
    key: 'name', label: 'Company', accessor: (c) => c.name, extra: 34, fixed: true,
    xget: (c) => c.name || '',
    xextra: [{ key: 'website', label: 'Website', get: (c) => c.domain || '' }],
    render: (c, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8, borderRight: '1px solid var(--border)' }}>
        <Logo name={c.name} color={c.logo} size={20} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{c.name}</div>
          {/* The domain reads as a hyperlink, so make it one — the company's
              website in a new tab, not the row-open navigation. */}
          {c.domain ? (
            <a href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer"
               onClick={(e) => e.stopPropagation()}
               title={`Open ${c.domain} in a new tab`}
               style={{ display: 'block', fontSize: 11, color: 'var(--accent-text)', textDecoration: 'none',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
               onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
               onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >{c.domain}</a>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-4)' }}>—</div>
          )}
        </div>
      </div>
    ),
  },
  {
    key: 'industry', label: 'Industry', accessor: (c) => c.industry, extra: 16,
    xget: (c) => (c.industry === '—' ? '' : (c.industry || '')),
    render: (c, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', borderRight: '1px solid var(--border)' }}>
        {/* displayAs already keeps the read view honest here; fallbackLabel is
            what names a deactivated industry inside the editor's dropdown. */}
        <window.EditableCell type="select" fill value={c.industryId} options={ctx.industryOpts} fallbackLabel={c._raw?.industry_name} locked={dcLocked(c, 'industry_id')}
          onSave={(industry_id) => ctx.onSave(c.id, { industry_id })}
          displayAs={c.industry !== '—'
            ? <Pill hue="teal" sz="sm">{c.industry}</Pill>
            : <Pill hue={c.pipelineState === 'lead_pool' ? 'amber' : 'slate'} sz="sm">{c.pipelineState === 'lead_pool' ? 'needs review' : '—'}</Pill>} />
      </div>
    ),
  },
  {
    // Lead source (142): where the company first came from. Ships hidden —
    // opt in from the Columns menu. Editable in place; picking a channel
    // here stamps today's lead date and fills the company's source-less deals.
    key: 'leadSource', label: 'Lead source', def: false, extra: 16,
    desc: 'Where this lead first came from — a workshop, a referral partner, inbound… Set once; deals inherit it.',
    accessor: (c) => (c.leadSource === '—' ? '' : (c.leadSource || '')),
    xget: (c) => (c.leadSource === '—' ? '' : (c.leadSource || '')),
    xextra: [{ key: 'lead_event', label: 'Lead event', get: (c) => c.leadEvent || '' }],
    render: (c, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', borderRight: '1px solid var(--border)' }}>
        <window.EditableCell type="select" fill value={c.leadSourceId} options={ctx.leadSourceOpts} fallbackLabel={c._raw?.lead_source_name}
          onSave={(lead_source_id) => ctx.onSave(c.id, { lead_source_id })}
          displayAs={c.leadSourceId
            ? <window.LeadSourcePill name={c.leadSource} group={c.leadSourceGroup} title={c.leadEvent || undefined} />
            : undefined} />
      </div>
    ),
  },
  {
    key: 'revenue', label: 'Revenue', align: 'right', accessor: (c) => fmtMoney(c.revenue),
    xget: (c) => c.revenue ?? '',
    render: (c, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', justifyContent: 'flex-end', fontSize: 12.5, fontWeight: 500, borderRight: '1px solid var(--border)' }} className="num">
        <window.EditableCell type="currency" fill align="right" value={c.revenue} locked={dcLocked(c, 'revenue_est')} onSave={(revenue_est) => ctx.onSave(c.id, { revenue_est })} />
      </div>
    ),
  },
  {
    key: 'ebitda', label: 'EBITDA', align: 'right', accessor: (c) => fmtMoney(c.ebitda),
    xget: (c) => c.ebitda ?? '',
    render: (c, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', justifyContent: 'flex-end', fontSize: 12, color: 'var(--text-2)', borderRight: '1px solid var(--border)' }} className="num">
        <window.EditableCell type="currency" fill align="right" value={c.ebitda} locked={dcLocked(c, 'ebitda_est')} onSave={(ebitda_est) => ctx.onSave(c.id, { ebitda_est })} />
      </div>
    ),
  },
  {
    key: 'interactions', label: 'Interactions', align: 'right', accessor: (c) => String(c.interactions || 0),
    xget: (c) => c.interactions ?? 0,
    xextra: [{ key: 'last_interaction', label: 'Last interaction', get: (c) => c.lastInteractionAt || '' }],
    render: (c, ctx, col) => (
      <div key={col.key} title={c.lastInteractionAt ? `Last interaction: ${window.fmtDate(c.lastInteractionAt)}` : ''}
        style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 12, color: c.interactions ? 'var(--text-2)' : 'var(--text-4)', borderRight: '1px solid var(--border)' }} className="num">
        {c.interactions || 0}
      </div>
    ),
  },
  {
    // Website visits from anyone at the company (app/web_rollups.py) — read-only.
    key: 'webVisits', label: 'Website visits', align: 'right', accessor: (c) => String(c.webVisits || 0),
    xget: (c) => c.webVisits ?? 0,
    xextra: [{ key: 'last_web_visit', label: 'Last website visit', get: (c) => c.lastWebVisitAt || '' }],
    render: (c, ctx, col) => (
      <div key={col.key} title={c.lastWebVisitAt ? `Last website visit: ${window.fmtDate(c.lastWebVisitAt)}` : ''}
        style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 12, color: c.webVisits ? 'var(--text-2)' : 'var(--text-4)', borderRight: '1px solid var(--border)' }} className="num">
        {c.webVisits || 0}
      </div>
    ),
  },
  {
    key: 'hq', label: 'HQ', accessor: (c) => c.hq,
    xget: (c) => (c.hq === '—' ? '' : (c.hq || '')),
    xextra: [
      { key: 'hq_city',  label: 'HQ city',  get: (c) => c._raw?.hq_city || '' },
      { key: 'hq_state', label: 'HQ state', get: (c) => c._raw?.hq_state || '' },
    ],
    render: (c, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text-2)', borderRight: '1px solid var(--border)' }}>{c.hq}</div>
    ),
  },
  {
    key: 'owner', label: 'Owner', accessor: (c) => c.owner, extra: 26,
    xget: (c) => (c.owner === '—' ? '' : (c.owner || '')),
    render: (c, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', gap: 6, borderRight: '1px solid var(--border)' }}>
        <window.EditableCell type="user" fill value={c._raw?.assigned_user_id || null} users={ctx.members || []} fallbackLabel={c._raw?.assigned_user_name} locked={dcLocked(c, 'assigned_user_id')}
          onSave={(assigned_user_id) => window.saveOwnerWithScope({
            kind: 'company', field: 'assigned_user_id', value: assigned_user_id, users: ctx.members || [],
            companyName: c.name, save: (patch) => ctx.onSave(c.id, patch),
          })} />
      </div>
    ),
  },
  {
    key: 'created', label: 'Created', accessor: (c) => window.fmtDate(window.recordCreated(c._raw)),
    xget: (c) => window.recordCreated(c._raw) || '',
    render: (c, ctx, col) => <window.RecordDateCell key={col.key} row={c._raw} kind="created" w={col.w} border />,
  },
  {
    key: 'updated', label: 'Updated', accessor: (c) => window.fmtDate(window.recordModified(c._raw)),
    xget: (c) => window.recordModified(c._raw) || '',
    render: (c, ctx, col) => <window.RecordDateCell key={col.key} row={c._raw} kind="modified" w={col.w} border last={col.last} />,
  },

  // ── Off by default: fields the API already returns that the grid never had
  // room for. One click away in the Columns menu.
  ...[
    { key: 'website',       label: 'Website',        group: 'Firmographics', text: (c) => c.domain },
    { key: 'recordId',      label: 'ID',             group: 'Firmographics', text: (c) => c.dealcloudId,
      desc: 'The company’s ID — the number on its page, in #/id/<n> links and in imports.' },
    { key: 'hqCity',        label: 'HQ city',        group: 'Firmographics', text: (c) => (c._raw || {}).hq_city },
    { key: 'hqState',       label: 'HQ state',       group: 'Firmographics', text: (c) => (c._raw || {}).hq_state },
    { key: 'team',          label: 'Team',           group: 'Ownership',     text: (c) => c.team },
    { key: 'pipelineState', label: 'Pipeline state', group: 'Pipeline',      text: (c) => c.pipelineState },
    { key: 'trackerStage',  label: 'Tracker stage',  group: 'Pipeline',      text: (c) => c.trackerStage,
      desc: "Mirrors DealCloud's TargetTrackerStage — free text." },
    { key: 'lastInteraction', label: 'Last interaction', group: 'Activity',  date: (c) => c.lastInteractionAt },
    { key: 'lastWebVisit',  label: 'Last website visit', group: 'Activity', date: (c) => c.lastWebVisitAt },
  ].map((c) => ({
    key: c.key, label: c.label, group: c.group, desc: c.desc, align: c.align, def: false,
    accessor: (r) => (c.date ? (window.fmtDate(c.date(r)) || '') : (c.text(r) || '')),
    // Export the underlying value (ISO dates, bare numbers), not the rendered one.
    xget: (r) => (c.date ? (c.date(r) || '') : (c.text(r) || '')),
    render: (r, ctx, col) => {
      const v = c.date ? window.fmtDate(c.date(r)) : c.text(r);
      return (
        <div key={col.key} style={{
          width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center',
          justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
          fontSize: 11.5, color: v ? 'var(--text-2)' : 'var(--text-4)',
          borderRight: col.last ? 'none' : '1px solid var(--border)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} className={c.align === 'right' ? 'num' : ''}>
          {v || '—'}
        </div>
      );
    },
  })),
];

// Memoized row — an inline cell edit swaps ONE row object in items, so only
// that row re-renders. Without this, every EditableCell in every loaded row
// re-ran per edit (1000s of rows after scrolling).
const CompanyRow = React.memo(({ c, checked, cols, cellCtx, onOpen, onToggle }) => {
  return (
    <div onClick={() => onOpen && onOpen(c.id)} style={{
      display: 'flex', minHeight: 38, borderBottom: '1px solid var(--border)',
      background: checked ? 'var(--accent-soft)' : 'var(--surface)',
      transition: 'background .1s', cursor: onOpen ? 'pointer' : 'default',
    }}
      onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'var(--surface)'; }}
    >
      <div style={{ width: 36, padding: '0 10px', display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={checked} onChange={() => onToggle(c.id)} style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
      </div>
      {cols.map((col) => col.render(c, cellCtx, col))}
    </div>
  );
});

// Header actions on a touch device (iPad): one wrapping row that can shrink,
// so nine buttons fold onto a second line instead of running off the page.
// Desktop gets the bare fragment, exactly as before.
const TouchWrap = ({ on, children }) => (on
  ? <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6, minWidth: 0 }}>{children}</div>
  : <>{children}</>);

const CompaniesView = ({ setView, setCompanyId }) => {
  // iPad: the header actions wrap and the tab strip scrolls instead of
  // overflowing the page (desktop layout is untouched).
  const device = window.useDevice();
  const [tab, setTab] = React.useState('active');
  const [selected, setSelected] = React.useState(() => new Set());
  const [promoting, setPromoting] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [industries, setIndustries] = React.useState(null);
  const [leadSources, setLeadSources] = React.useState([]);
  const [members, setMembers] = React.useState(null);
  const [formulaDefs, setFormulaDefs] = React.useState([]);
  const [items, setItems] = React.useState(null);    // current tab's rows
  const [counts, setCounts] = React.useState({ active: 0, lead_pool: 0, archived: 0, all: 0 });

  // Sort + filter state — same shape as DealsView. Default sort = revenue desc
  // (matches the dealmaker workflow: "show me my biggest companies first").
  const [sortKey, setSortKey] = React.useState('revenue');
  const [sortDir, setSortDir] = React.useState('desc');
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [saveViewOpen, setSaveViewOpen] = React.useState(false);
  const [importing, setImporting] = React.useState(false);  // bulk update from a file
  const [bulkEditing, setBulkEditing] = React.useState(false);  // set a field on the selected rows
  const [filters, setFilters] = React.useState({
    industries: [],   // multi-select chips (industry ids)
    leadSources: [],  // multi-select chips (lead_source ids, 142)
    tracker:    [],   // multi-select chips (free-text DC tracker stage labels)
    signal:     '',   // single signal slug (bs_call, won, etc.)
    owner:      '',   // assigned_user_id
    minRevenue: '',
  });

  React.useEffect(() => {
    Promise.all([
      window.AtlasApi.listIndustries().catch(() => []),
      window.AtlasData.loadTeamMembers(),
      window.AtlasApi.listFieldDefinitions('company').catch(() => []),
      window.AtlasApi.listLeadSources().catch(() => []),
    ]).then(([inds, mems, defs, srcs]) => {
      setIndustries(inds || []);
      setLeadSources(Array.isArray(srcs) ? srcs : []);
      setMembers(Array.isArray(mems) ? mems : []);
      setFormulaDefs((defs || []).filter((d) => d.field_type === 'formula' && d.active));
    });
  }, []);

  // Per-tab counts via cheap server-side aggregates. One call per tab is fine
  // because pipeline_state is a tiny enum.
  const reloadCounts = React.useCallback(async () => {
    const fetchCount = (state) =>
      window.AtlasApi.listCompanies({ my_team: true, limit: 1, pipeline_state: state })
        .then((r) => r?.total ?? 0).catch(() => 0);
    const [a, l, r, all] = await Promise.all([
      fetchCount('active'), fetchCount('lead_pool'), fetchCount('archived'),
      window.AtlasApi.listCompanies({ my_team: true, limit: 1 }).then((r) => r?.total ?? 0).catch(() => 0),
    ]);
    setCounts({ active: a, lead_pool: l, archived: r, all });
  }, []);
  React.useEffect(() => { reloadCounts(); }, [reloadCounts]);

  // Cursor pagination — single consistent limit so the server's
  // offset=(page-1)*limit math lines up with our cumulative items.length.
  // Mixing limits (60 first, 120 after) skipped rows 60-120 and caused
  // runaway pagination because items.length could never catch total.
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [paging, setPaging] = React.useState(false);
  // Hard-stop flag: flips true when a fetch returns 0 rows OR we hit total.
  // Belt-and-braces against any case where the server's `total` overstates
  // what's actually retrievable for the current filter combo.
  const [loadedAll, setLoadedAll] = React.useState(false);
  const sentinelRef = React.useRef(null);
  const PAGE_SIZE = 120;

  // Stale-response guard: reloads bump the seq; a slow response from an
  // older filter/tab/sort combination is dropped instead of overwriting
  // fresh rows (the debounced-search race class of bug).
  const reqSeq = React.useRef(0);

  // Filter-only params, shared by the pager and Export's whole-view fetch.
  // Filters + sort are server-side: they must apply to the whole 312k-row
  // scope, not just the pages the client happened to load. Only the
  // lookup-column sorts (industry/owner) remain client-side.
  const buildParams = React.useCallback(() => {
    const params = { my_team: true };
    if (tab !== 'all') params.pipeline_state = tab;
    if (filters.industries.length) params.industry_id = filters.industries;
    if ((filters.leadSources || []).length) params.lead_source_id = filters.leadSources;
    if (filters.tracker.length) {
      params.tracker_stage = filters.tracker.map((t) => (t === '(unset)' ? 'unset' : t));
    }
    if (filters.signal) params.signal = filters.signal;
    if (filters.owner) params.assigned_user_id = filters.owner;
    const minRev = Number(filters.minRevenue);
    if (filters.minRevenue && !Number.isNaN(minRev) && minRev > 0) params.min_revenue = minRev;
    if (COMPANY_SERVER_SORT[sortKey]) {
      params.order_by = COMPANY_SERVER_SORT[sortKey];
      params.order_dir = sortDir;
    }
    return params;
  }, [tab, filters, sortKey, sortDir]);

  const fetchPage = React.useCallback(async (p, append) => {
    const seq = ++reqSeq.current;
    setPaging(true);
    try {
      const res = await window.AtlasApi.listCompanies({ ...buildParams(), page: p, limit: PAGE_SIZE });
      if (seq !== reqSeq.current) return;   // superseded — drop silently
      const rows = ((res && res.items) || []).map(window.AtlasData.__adaptCompany);
      const newTotal = res?.total ?? rows.length;
      setItems((prev) => {
        const next = append && prev ? [...prev, ...rows] : rows;
        if (rows.length === 0 || next.length >= newTotal) {
          setLoadedAll(true);
        }
        return next;
      });
      setTotal(newTotal);
      setPage(p);
    } catch {
      if (seq !== reqSeq.current) return;
      if (!append) { setItems([]); setLoadedAll(true); }
    } finally {
      if (seq === reqSeq.current) setPaging(false);
    }
  }, [buildParams]);

  const reloadItems = React.useCallback(() => {
    setItems(null);
    setSelected(new Set());
    setPage(1);
    setLoadedAll(false);
    return fetchPage(1, false);
  }, [fetchPage]);
  React.useEffect(() => { reloadItems(); }, [reloadItems]);

  // Auto-load next page on scroll-to-near-bottom. Both gates must be true:
  // there's still data per the server's count AND the last fetch didn't
  // come back empty.
  const hasMore = items != null && !loadedAll && items.length < total;
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !paging) {
        fetchPage(page + 1, true);
      }
    }, { rootMargin: '240px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, paging, page, fetchPage]);

  // Filters + column sorts are applied server-side in fetchPage (whole-scope
  // correctness). Only the lookup-column sorts (industry/owner name) sort
  // client-side over the loaded pages — the server can't order by them.
  const rows = React.useMemo(() => {
    let r = items || [];
    if (sortKey && !COMPANY_SERVER_SORT[sortKey]) {
      r = [...r].sort(window.makeComparator(sortKey, sortDir, COMPANY_SORT_EXTRACT));
    }
    return r;
  }, [items, sortKey, sortDir]);

  const toggleSort = React.useMemo(
    () => window.makeToggleSort(setSortKey, setSortDir),
    [setSortKey, setSortDir]
  );

  // Inline cell editing — reuse the shared EditableCell primitive. onSave PATCHes
  // the company and swaps the freshly-adapted row into local state (optimistic);
  // EditableCell reverts its own draft if the PATCH throws. Only the edited
  // row object changes identity, so the memoized CompanyRow re-renders alone.
  const saveCompany = React.useCallback(async (id, patch) => {
    const updated = await window.AtlasApi.updateCompany(id, patch);
    setItems((prev) => (prev || []).map((x) => (x.id === id ? window.AtlasData.__adaptCompany(updated) : x)));
    return updated;
  }, []);

  // "Save as view" — capture the grid's current filters/sort/tab as a saved-view
  // spec over the raw `companies` source. Only filters that map to real columns
  // are captured; computed ones (the `signal` filter) are skipped. Columns are
  // the editable companies fields so the view is inline-editable.
  const captureViewSpec = React.useCallback(() => {
    const f = [];
    if ((filters.industries || []).length) f.push({ column: 'industry_id', op: 'in', value: filters.industries });
    if ((filters.leadSources || []).length) f.push({ column: 'lead_source_id', op: 'in', value: filters.leadSources });
    const trk = (filters.tracker || []).filter((t) => t && t !== '(unset)');
    if (trk.length) f.push({ column: 'tracker_stage', op: 'in', value: trk });
    if (filters.owner) f.push({ column: 'assigned_user_id', op: '=', value: filters.owner });
    const minRev = Number(filters.minRevenue);
    if (filters.minRevenue && !Number.isNaN(minRev) && minRev > 0) f.push({ column: 'revenue_est', op: '>=', value: minRev });
    if (tab && tab !== 'all') f.push({ column: 'pipeline_state', op: '=', value: tab });
    const SORT = { revenue: 'revenue_est', name: 'name', hq: 'hq_city', interactions: 'interaction_count' };
    const sort = SORT[sortKey] ? { by: SORT[sortKey], dir: sortDir } : { by: 'revenue_est', dir: 'desc' };
    return {
      source_entity: 'companies',
      spec: {
        // `industry` + `owner` are lookup (display) columns — readable names
        // resolved from industry_id / assigned_user_id, rendered read-only.
        columns: ['name', 'industry', 'owner', 'role', 'pipeline_state', 'hq_city', 'hq_state', 'revenue_est', 'ebitda_est', 'interaction_count', 'last_interaction_at'],
        filters: f, sort, limit: 500,
      },
    };
  }, [filters, sortKey, sortDir, tab]);
  const industryOpts = React.useMemo(
    () => [{ value: null, label: '— Unassigned —' }, ...((industries || []).map((i) => ({ value: i.id, label: i.name })))],
    [industries]);
  const leadSourceOpts = React.useMemo(
    () => [{ value: null, label: '— Not recorded —' }, ...((leadSources || []).map((s) => ({ value: s.id, label: s.name })))],
    [leadSources]);

  // Per-user column layout (105).
  // Admin-defined formula/AI fields join the catalog, so they hide and
  // reorder like any built-in column instead of being pinned to the far right.
  const companyCatalog = React.useMemo(
    () => [...COMPANY_COLUMNS, ...window.formulaColumns(formulaDefs)],
    [formulaDefs]);
  const columns = window.useColumnPrefs('companies', companyCatalog, {
    rows: items || [], fitMax: 400,
  });
  const startResize = columns.startResize;
  // One object so a memoized row can't capture a stale per-cell closure.
  const cellCtx = React.useMemo(
    () => ({ industryOpts, leadSourceOpts, members, onSave: saveCompany }),
    [industryOpts, leadSourceOpts, members, saveCompany]);

  const visibleIds = rows.map(r => r.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else             visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const toggleOne = React.useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Export the selection, or with nothing selected the whole current view.
  // The whole-view path refetches in one call; companies is a 312K-row
  // table, so it can top out at the server's 10K cap — the menu says so
  // (capNote) rather than let a truncated file read as complete.
  const EXPORT_CAP = 10000;
  const doExport = React.useCallback(async (format) => {
    let data;
    if (selected.size) {
      data = rows.filter((c) => selected.has(c.id));
    } else if (loadedAll) {
      data = rows;
    } else {
      const res = await window.AtlasApi.listCompanies({ ...buildParams(), page: 1, limit: EXPORT_CAP });
      data = ((res && res.items) || []).map(window.AtlasData.__adaptCompany);
      if (sortKey && !COMPANY_SERVER_SORT[sortKey]) {
        data = [...data].sort(window.makeComparator(sortKey, sortDir, COMPANY_SORT_EXTRACT));
      }
    }
    // The file mirrors the grid: the columns you can see, in your order.
    const xcols = window.exportColumnsFor(columns.cols, COMPANY_EXPORT_ALWAYS);
    const records = window.exportRecords(xcols, data);
    const name = `companies-${tab}-${window.localISODate()}`;
    if (format === 'csv') window.exportCsv(`${name}.csv`, xcols, records);
    else window.exportJson(`${name}.json`, records);
  }, [selected, rows, loadedAll, buildParams, sortKey, sortDir, tab, columns.cols]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Companies']}
        title="Companies"
        meta={items === null ? 'Loading…' : `${total.toLocaleString()} of ${counts.all.toLocaleString()} records`}
        actions={(
          <TouchWrap on={device.touch}>
            {selected.size > 0 && tab === 'lead_pool' && (
              <Btn size="sm" variant="accent" icon={<Icon.ChevronR size={12} />} onClick={() => setPromoting(true)}>
                Promote {selected.size}
              </Btn>
            )}
            {selected.size > 0 && (
              <Btn size="sm" variant="outline" icon={<Icon.Edit size={12} />} onClick={() => setBulkEditing(true)}
                title="Set one field on every selected company">
                Edit {selected.size}
              </Btn>
            )}
            <div style={{ position: 'relative' }}>
              <Btn size="sm" variant={window.activeFilterCount(filters) ? 'accent' : 'outline'}
                icon={<Icon.Filter size={12} />}
                onClick={() => setFilterOpen((v) => !v)}>
                Filter
                {window.activeFilterCount(filters) > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 10.5, opacity: 0.85 }}>
                    {window.activeFilterCount(filters)}
                  </span>
                )}
              </Btn>
              {filterOpen && (
                <window.FilterPopover
                  filters={filters}
                  setFilters={setFilters}
                  onClose={() => setFilterOpen(false)}
                  spec={[
                    { key: 'industries', label: 'Industry', type: 'chips',
                      options: (industries || []).map((i) => ({ value: i.id, label: i.name })) },
                    { key: 'leadSources', label: 'Lead source', type: 'chips',
                      options: (leadSources || []).map((s) => ({ value: s.id, label: s.name })),
                      hint: 'Where the lead first came from. Companies with none recorded never match.' },
                    { key: 'tracker',    label: 'Tracker stage', type: 'chips',
                      // Build the option list from the loaded rows so the chips
                      // only show stages that actually exist in this scope.
                      options: Array.from(new Set((items || []).map((c) => c.trackerStage || '(unset)')))
                        .sort()
                        .map((v) => ({ value: v, label: v })) },
                    { key: 'signal',     label: 'Has signal', type: 'select',
                      placeholder: 'Any signal',
                      options: [
                        { value: 'intro_or_ds_call', label: 'D/S call' },
                        { value: 'bs_call',          label: 'B/S call' },
                        { value: 'pb_blast',         label: 'PB Blast' },
                        { value: 'nda_signed',       label: 'NDA signed' },
                        { value: 'loi',              label: 'LOI' },
                        { value: 'closing',          label: 'Closing' },
                        { value: 'won',              label: 'Won' },
                      ] },
                    { key: 'owner',      label: 'Owner', type: 'select',
                      placeholder: 'Anyone',
                      options: (members || []).filter((u) => u.active !== false)
                        .map((u) => ({ value: u.id, label: u.full_name || u.email })) },
                    { key: 'minRevenue', label: 'Min. revenue ($)', type: 'number',
                      placeholder: 'e.g. 1000000' },
                  ]}
                />
              )}
            </div>
            <Btn size="sm" variant="outline" icon={<Icon.Sort size={12} />}
              onClick={() => { setSortKey('revenue'); setSortDir('desc'); }}
              title="Click a column header to sort. This resets to default (Revenue · desc).">
              {sortKey ? `Sort: ${sortKey} ${sortDir === 'desc' ? '↓' : '↑'}` : 'Sort'}
            </Btn>
            <window.ExportMenu
              disabled={items === null || (!rows.length && !selected.size)}
              selectedCount={selected.size}
              viewCount={loadedAll ? rows.length : total}
              capNote={!loadedAll && total > EXPORT_CAP
                ? `Caps at the first ${EXPORT_CAP.toLocaleString()} rows by the current sort — narrow the filters for a complete file.`
                : null}
              onExport={doExport}
            />
            <Btn size="sm" variant="outline" icon={<Icon.Upload size={12} />}
              onClick={() => setImporting(true)}
              title="Update companies from a CSV or Excel file — preview before anything changes">Import</Btn>
            <window.ColumnsMenu
              catalog={companyCatalog}
              cols={columns.cols}
              toggle={columns.toggle}
              move={columns.move}
              reset={columns.reset}
            />
            <Btn size="sm" variant="outline" icon={<Icon.Table size={12} />}
              onClick={() => setSaveViewOpen(true)}
              title="Save this filtered table as a reusable, shareable view">Save as view</Btn>
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />}
              onClick={() => setCreating(true)}>New company</Btn>
          </TouchWrap>
        )}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: '0 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36,
        ...(device.touch ? { overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' } : {}) }}>
        {[
          ['active',    'Active',    counts.active],
          ['lead_pool', 'Lead pool', counts.lead_pool],
          ['archived',  'Archived',  counts.archived],
          ['all',       'All',       counts.all],
        ].map(([k, l, n]) => {
          const active = tab === k;
          return (
            <button key={k} onClick={() => { setTab(k); setSelected(new Set()); }} style={{
              padding: '0 10px', height: 36, border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-3)',
              borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
              ...(device.touch ? { flexShrink: 0 } : {}),
            }}>
              {l}<span style={{ fontSize: 10.5, color: 'var(--text-4)' }} className="num">{n}</span>
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ minWidth: 'max-content' }}>
          <div style={{ display: 'flex', height: 30, borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', position: 'sticky', top: 0, zIndex: 1, fontSize: 11, color: 'var(--text-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <div style={{ width: 36, padding: '0 10px' }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
            </div>
            {columns.cols.map((c) => (
              <window.SortableHeader key={c.key} col={c}
                sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                sortable={Boolean(COMPANY_SORT_EXTRACT[c.key])}
                onResizeStart={startResize(c.key)}
              />
            ))}
          </div>
          {items === null && (
            <SkeletonRows rows={10} variant="company" />
          )}
          <window.WindowedRows rows={rows} renderRow={(c) => (
            <CompanyRow key={c.id} c={c} checked={selected.has(c.id)}
              cols={columns.cols} cellCtx={cellCtx}
              onOpen={setCompanyId}
              onToggle={toggleOne} />
          )} />
          {hasMore && items !== null && (
            <div ref={sentinelRef}>
              <SkeletonRows rows={3} variant="company" />
            </div>
          )}
          {!hasMore && items !== null && items.length > 0 && (
            <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--text-4)', textAlign: 'center' }}>
              · end of list · {items.length.toLocaleString()} of {total.toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {promoting && (
        <BulkPromoteDrawer
          ids={[...selected]}
          industries={industries || []}
          members={members || []}
          onClose={() => setPromoting(false)}
          onDone={() => {
            setPromoting(false);
            setSelected(new Set());
            reloadItems();
            reloadCounts();
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />
      )}

      {window.CompanyImportDrawer && (
        <window.CompanyImportDrawer
          open={importing}
          onClose={() => setImporting(false)}
          onDone={() => { reloadItems(); reloadCounts(); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }}
        />
      )}

      {window.BulkEditDrawer && (
        <window.BulkEditDrawer open={bulkEditing} kind="companies" ids={[...selected]}
          onClose={() => setBulkEditing(false)}
          onDone={() => { setSelected(new Set()); reloadItems(); reloadCounts(); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }} />
      )}

      {creating && (
        <NewCompanyDrawer
          industries={industries || []}
          members={members || []}
          onClose={() => setCreating(false)}
          onCreated={(company) => {
            setCreating(false);
            reloadItems();
            reloadCounts();
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
            if (company?.id) { setCompanyId(company.id); setView('company'); }
          }}
        />
      )}

      {window.SaveViewDialog && (
        <window.SaveViewDialog
          open={saveViewOpen}
          onClose={() => setSaveViewOpen(false)}
          capture={captureViewSpec}
          onSaved={(v) => { if (v?.id && window.AtlasNav?.goToView) window.AtlasNav.goToView(v.id); }}
        />
      )}
    </div>
  );
};

const BulkPromoteDrawer = ({ ids, industries, members, onClose, onDone }) => {
  const [industryId, setIndustryId] = React.useState('');
  const [ownerId, setOwnerId]       = React.useState('');
  const [target, setTarget]         = React.useState('active');
  const [busy, setBusy]             = React.useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await window.AtlasApi.bulkPromoteCompanies({
        company_ids: ids,
        industry_id: industryId || null,
        assigned_user_id: ownerId || null,
        target_state: target,
      });
      onDone();
    } catch (e) {
      pushToast({ type: 'error', title: 'Promote failed', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={`Promote ${ids.length} companies`}
      footer={(
        <>
          <Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={submit} disabled={busy}>
            {busy ? 'Promoting…' : `Promote ${ids.length}`}
          </Btn>
        </>
      )}
    >
      <Field label="Target state">
        <Select value={target} onChange={(e) => setTarget(e.target.value)}
          options={[
            { value: 'active',    label: 'Active (visible to dealmakers)' },
            { value: 'archived',  label: 'Archived (hide permanently)' },
            { value: 'lead_pool', label: 'Lead pool (keep here)' },
          ]}
        />
      </Field>
      <Field label="Industry" hint="Optional — applies to every selected company.">
        <Select value={industryId} onChange={(e) => setIndustryId(e.target.value)}
          options={[
            { value: '', label: '(no change)' },
            ...industries.map(i => ({ value: i.id, label: `${i.name}${i.assigned_user_name ? ' — ' + i.assigned_user_name : ''}` })),
          ]}
        />
      </Field>
      <Field label="Assign to" hint="Optional — typically the dealmaker who owns the industry.">
        <Select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
          options={[
            { value: '', label: '(no change)' },
            ...members.filter(window.isOwnerEligible)
              .map(m => ({ value: m.id, label: `${m.full_name} (${(m.role || '').replace(/_/g, ' ')})` })),
          ]}
        />
      </Field>
    </Drawer>
  );
};

// `initialName` seeds the name from a search query ("Reveille Wealth") so
// typeaheads can offer "create this company" without retyping. `zIndex`
// forwards to Drawer so the stack renders above modals when needed.
const NewCompanyDrawer = ({ industries, members, onClose, onCreated, initialName, zIndex }) => {
  const [name, setName]           = React.useState(initialName || '');
  const [website, setWebsite]     = React.useState('');
  // Create-time duplicate guard: as name/website are typed, ask the server
  // for likely-existing companies (domain / exact / similar name). Advisory —
  // "Use this instead" hands the existing record to onCreated without
  // creating anything; the user can always still create.
  const [dupes, setDupes]         = React.useState([]);
  const [state, setState]         = React.useState('active');
  const [industryId, setIndustryId] = React.useState('');
  const [leadSourceId, setLeadSourceId] = React.useState('');
  const [leadSources, setLeadSources]   = React.useState([]);
  const [role, setRole]           = React.useState('seller');
  const [ownerId, setOwnerId]     = React.useState('');
  const [hqCity, setHqCity]       = React.useState('');
  const [hqState, setHqState]     = React.useState('');
  const [country, setCountry]     = React.useState('United States');
  // Numbers (or null), not strings — MoneyInput parses the shorthand and
  // hands back the absolute figure.
  const [revenue, setRevenue]     = React.useState(null);
  const [ebitda, setEbitda]       = React.useState(null);
  const [busy, setBusy]           = React.useState(false);

  React.useEffect(() => {
    const nm = name.trim(), ws = website.trim();
    if (nm.length < 3 && !ws) { setDupes([]); return; }
    let stale = false;
    const t = setTimeout(() => {
      window.AtlasApi.checkCompanyDuplicates({ name: nm || undefined, website: ws || undefined })
        .then((r) => { if (!stale) setDupes((r && r.matches) || []); })
        .catch(() => { if (!stale) setDupes([]); });
    }, 350);
    return () => { stale = true; clearTimeout(t); };
  }, [name, website]);

  React.useEffect(() => {
    window.AtlasApi.listLeadSources().then((s) => setLeadSources(Array.isArray(s) ? s : [])).catch(() => {});
  }, []);

  const DUPE_REASON = {
    domain:       'same website',
    name_exact:   'same name',
    name_similar: 'similar name',
  };

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const company = await window.AtlasApi.createCompany({
        name: name.trim(),
        website: website.trim() || null,
        pipeline_state: state,
        industry_id: industryId || null,
        lead_source_id: leadSourceId || null,
        role,
        assigned_user_id: ownerId || null,
        hq_city: hqCity.trim() || null,
        hq_state: hqState.trim() || null,
        country: country.trim() || null,
        revenue_est: revenue || null,
        ebitda_est: ebitda || null,
      });
      pushToast({ type: 'success', title: 'Company created', body: name.trim() });
      onCreated(company);
    } catch (e) {
      pushToast({ type: 'error', title: 'Create failed', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      zIndex={zIndex}
      title="New company"
      footer={(
        <>
          <Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={submit} disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create company'}
          </Btn>
        </>
      )}
    >
      <Field label="Name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Acme Industrial Services" autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
      </Field>
      <Field label="Website" hint="Primary domain.">
        <TextInput value={website} onChange={(e) => setWebsite(e.target.value)}
          placeholder="acme.com" />
      </Field>
      {dupes.length > 0 && (
        <div style={{
          border: '1px solid var(--amber-border, var(--border-strong))', borderRadius: 7,
          background: 'var(--amber-soft, var(--surface-2))', padding: '8px 10px', marginBottom: 12,
        }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
            May already exist in Atlas — link instead of creating a duplicate:
          </div>
          {dupes.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span style={{ fontWeight: 600 }}>{d.name}</span>
                <span style={{ color: 'var(--text-4)', marginLeft: 6 }}>
                  {[d.website, [d.hq_city, d.hq_state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                </span>
              </span>
              <Pill hue="slate">{DUPE_REASON[d.reason] || d.reason}</Pill>
              <Btn size="xs" variant="outline" disabled={busy}
                onClick={() => { pushToast({ type: 'success', title: 'Linked existing company', body: d.name }); onCreated(d); }}>
                Use this
              </Btn>
            </div>
          ))}
        </div>
      )}
      <Field label="Pipeline state">
        <Select value={state} onChange={(e) => setState(e.target.value)}
          options={[
            { value: 'active',    label: 'Active (owned, visible to dealmakers)' },
            { value: 'lead_pool', label: 'Lead pool (uncategorized)' },
            { value: 'archived',  label: 'Archived' },
          ]}
        />
      </Field>
      <Field label="Industry" hint="Optional — drives coverage assignment.">
        <Select value={industryId} onChange={(e) => setIndustryId(e.target.value)}
          options={[
            { value: '', label: '(none)' },
            ...industries.map(i => ({ value: i.id, label: `${i.name}${i.assigned_user_name ? ' — ' + i.assigned_user_name : ''}` })),
          ]}
        />
      </Field>
      <Field label="Lead source" hint="Where this lead came from — a workshop, a referral partner, inbound… The lead date is stamped today; add the specific event on the company page.">
        <Select value={leadSourceId} onChange={(e) => setLeadSourceId(e.target.value)}
          options={[
            { value: '', label: '(not recorded)' },
            ...leadSources.map((s) => ({ value: s.id, label: s.name })),
          ]}
        />
      </Field>
      <Field label="Role">
        <Select value={role} onChange={(e) => setRole(e.target.value)}
          options={[
            { value: 'seller',  label: 'Seller (target)' },
            { value: 'buyer',   label: 'Buyer' },
            { value: 'both',    label: 'Both' },
            { value: 'partner', label: 'Partner' },
            { value: 'service_provider', label: 'Service provider (professional services)' },
          ]}
        />
      </Field>
      <Field label="Assign to" hint="Optional — the dealmaker who owns it.">
        <Select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
          options={[
            { value: '', label: '(unassigned)' },
            ...members.filter(window.isOwnerEligible)
              .map(m => ({ value: m.id, label: `${m.full_name} (${(m.role || '').replace(/_/g, ' ')})` })),
          ]}
        />
      </Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Field label="HQ city"><TextInput list={window.GEO.LIST_IDS.city} value={hqCity} onChange={(e) => setHqCity(e.target.value)} placeholder="e.g. Dallas" /></Field>
        </div>
        <div style={{ flex: 1 }}>
          {/* Full names, matching the DealCloud convention — "Texas", not "TX" */}
          <Field label="HQ state"><TextInput list={window.GEO.LIST_IDS.state} value={hqState} onChange={(e) => setHqState(e.target.value)} placeholder="e.g. Texas" /></Field>
        </div>
      </div>
      <Field label="Country"><TextInput list={window.GEO.LIST_IDS.country} value={country} onChange={(e) => setCountry(e.target.value)} /></Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Field label="Revenue (est, USD)"><MoneyInput value={revenue} onChange={setRevenue} placeholder="5000000 or 5M" /></Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="EBITDA (est, USD)"><MoneyInput value={ebitda} onChange={setEbitda} placeholder="1000000 or 1M" /></Field>
        </div>
      </div>
    </Drawer>
  );
};

window.CompaniesView = CompaniesView;
window.NewCompanyDrawer = NewCompanyDrawer;
